import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Play, CheckCircle2, Circle, MoreVertical, X,
  BookOpen, Music, Sparkles, Scroll, Anchor, Heart, Bell, Trash2, Share2, ScrollText, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import PageSubHeader from '@/components/PageSubHeader';
import { useSafeBack } from '@/utils/useSafeBack';
import { API_URL as API } from '@/utils/apiConfig';
import useCachedFetch from '@/hooks/useCachedFetch';

import { useTranslation } from 'react-i18next';

const ICONS = { BookOpen, Music, Sparkles, Scroll, Anchor, Heart };

export default function StudyPlanDetailPage() {
  const {
    t,
    i18n
  } = useTranslation();

  const { planId } = useParams();
  const navigate = useNavigate();
  const goBack = useSafeBack('/study-plans');
  const [starting, setStarting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const lang = (i18n.language || 'en').split('-')[0];

  // Shared cache key with StudyPlanDayPage — so opening a plan day
  // and coming back never triggers a spinner: both surfaces read
  // the same snapshot.
  const fetcher = useCallback(async () => {
    const res = await axios.get(`${API}/reading-plans/${planId}`, {
      params: { locale: lang },
      withCredentials: true,
    });
    return { plan: res.data?.plan || null, progress: res.data?.progress || null };
  }, [planId, lang]);
  const {
    data,
    isColdLoading,
    error: fetchError,
    mutate,
  } = useCachedFetch(planId ? `plan:${planId}:${lang}` : null, fetcher);
  const plan = data?.plan || null;
  const progress = data?.progress || null;

  if (fetchError && !data) {
    toast.error(fetchError?.response?.data?.detail || 'Failed to load plan');
    navigate('/study-plans', { replace: true });
  }

  const startPlan = async () => {
    setStarting(true);
    try {
      const res = await axios.post(`${API}/reading-plans/${planId}/start`, {}, { withCredentials: true });
      mutate((prev) => ({ ...(prev || {}), progress: res.data?.progress }));
      const target = res.data?.progress?.current_day || 1;
      navigate(`/study-plans/${planId}/day/${target}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start');
    } finally {
      setStarting(false);
    }
  };

  const leavePlan = async () => {
    if (!window.confirm('Leave this plan? Progress is saved if you come back.')) return;
    try {
      await axios.post(`${API}/reading-plans/${planId}/leave`, {}, { withCredentials: true });
      toast.success(t('generated.studyplandetailpage_plan_paused_aff3'));
      mutate((prev) => ({ ...(prev || {}), progress: null }));
      setShowMenu(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const deleteCustomPlan = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this custom plan? This is permanent — the plan and your progress will be removed.')) return;
    try {
      await axios.delete(`${API}/reading-plans/custom/${planId}`, { withCredentials: true });
      toast.success('Custom plan deleted');
      setShowMenu(false);
      navigate('/study-plans');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const shareCustomPlan = async () => {
    try {
      const res = await axios.post(
        `${API}/reading-plans/custom/${planId}/share`,
        {},
        { withCredentials: true }
      );
      const code = res.data?.share_code;
      const url = `${window.location.origin}/api/og/plans/join/${code}`;
      const shareText = `I built a study plan on "${plan?.title || 'Scripture'}" using Eili — want to try it?\n\n${url}`;
      // Prefer native share sheet where available (iOS / Android
      // Chrome), fall back to clipboard so desktop users still get a
      // usable link.
      try {
        if (navigator.share) {
          await navigator.share({ title: plan?.title || 'Custom study plan', text: shareText, url });
        } else {
          await navigator.clipboard.writeText(url);
          toast.success('Share link copied to clipboard');
        }
      } catch (nativeErr) {
        // User canceled the native share sheet — treat as a no-op.
        if (nativeErr?.name !== 'AbortError') {
          await navigator.clipboard.writeText(url);
          toast.success('Share link copied to clipboard');
        }
      }
      setShowMenu(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message || 'Could not create share link';
      toast.error(message);
    }
  };

  const Icon = useMemo(() => (plan ? (ICONS[plan.icon] || BookOpen) : BookOpen), [plan]);
  const cover = plan?.cover_color || 'var(--theme-accent)';
  const completed = new Set(progress?.completed_days || []);
  const completedCount = completed.size;
  const pct = plan ? Math.round((completedCount / plan.length_days) * 100) : 0;

  if (isColdLoading) {
    return (
      <div className="page-container flex items-center justify-center" style={{ background: 'var(--theme-background)' }}>
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-accent)' }} />
      </div>
    );
  }
  if (!plan) return null;

  const enrolled = !!progress;
  const finished = !!progress?.completed_at;

  return (
    <div className="page-container" style={{ background: 'var(--theme-background)' }}>
      <Header />
      <PageSubHeader
        onBack={goBack}
        title={plan.title}
        subtitle={`${plan.length_days} days · ${plan.short_description}`}
        backTestId="plan-detail-back"
        rightSlot={(enrolled || plan.is_custom) ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowMenu(true)}
            className="rounded-full"
            data-testid="plan-detail-menu"
          >
            <MoreVertical className="h-5 w-5" />
          </Button>
        ) : null}
      />
      <div className="page-content max-w-2xl lg:max-w-4xl mx-auto px-4 lg:px-8 pt-4 lg:pt-8 pb-24 w-full">
        {/* Hero */}
        <div className="rounded-3xl p-6 mb-5"
          style={{
            background: `linear-gradient(135deg, ${cover}22, ${cover}08)`,
            border: `1px solid ${cover}33`,
          }}
          data-testid="plan-detail-hero"
        >
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: `${cover}33`, border: `1px solid ${cover}55` }}
          >
            <Icon className="h-7 w-7" style={{ color: cover }} />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--theme-text)' }}>{plan.title}</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--theme-textSecondary)' }}>
            {plan.length_days} {t('generated.studyplandetailpage_days_09de')} {plan.short_description}
          </p>
          {/* Viral counter for custom plans — how many friends have
              started the plan via the sender's share link. Only shown
              to the owner and only when there's at least one accept
              to celebrate, so it doesn't feel empty on a fresh plan. */}
          {plan.is_custom && plan.share_accept_count > 0 && (
            <div
              className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full text-[11px] font-semibold"
              style={{ background: `${cover}22`, color: cover, border: `1px solid ${cover}44` }}
              data-testid="plan-share-accept-count"
            >
              <Sparkles className="h-3 w-3" />
              {plan.share_accept_count === 1
                ? '1 friend has started this'
                : `${plan.share_accept_count} friends have started this`}
            </div>
          )}
          {enrolled && (
            <div className="mt-4">
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--glass-bg-active)' }}>
                <div className="h-full transition-all" style={{ width: `${pct}%`, background: cover }} />
              </div>
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
                  {completedCount} of {plan.length_days} {t('generated.studyplandetailpage_days_complete_0410')} {pct}%
                                  </p>
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      const res = await axios.post(`${API}/reading-plans/${planId}/reminders`, {}, { withCredentials: true });
                      mutate((prev) => ({
                        ...(prev || {}),
                        progress: { ...(prev?.progress || {}), reminders_enabled: res.data.reminders_enabled },
                      }));
                      toast.success(res.data.reminders_enabled ? 'Daily reminders on' : 'Daily reminders off');
                    } catch { toast.error(t('generated.studyplandetailpage_failed_to_update_da9b')); }
                  }}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-all"
                  style={{
                    background: (progress.reminders_enabled !== false) ? `${cover}22` : 'var(--glass-bg)',
                    color: (progress.reminders_enabled !== false) ? cover : 'var(--theme-textSecondary)',
                    border: `1px solid ${(progress.reminders_enabled !== false) ? cover + '55' : 'var(--glass-border)'}`,
                  }}
                  data-testid="plan-reminder-toggle"
                >
                  <Bell className="h-3 w-3" />
                  {(progress.reminders_enabled !== false) ? 'Reminders on' : 'Reminders off'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Description */}
        <p className="text-sm mb-6 px-1" style={{ color: 'var(--theme-textSecondary)', lineHeight: 1.6 }}>
          {plan.description}
        </p>

        {/* Primary CTA */}
        {!enrolled ? (
          <Button
            onClick={startPlan}
            disabled={starting}
            className="w-full rounded-2xl py-6 text-base font-semibold gap-2 mb-6"
            style={{ background: cover, color: '#fff' }}
            data-testid="plan-start-btn"
          >
            {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            {t('generated.studyplandetailpage_start_plan_0dea')}
          </Button>
        ) : !finished ? (
          <Button
            onClick={() => navigate(`/study-plans/${planId}/day/${progress.current_day}`)}
            className="w-full rounded-2xl py-6 text-base font-semibold gap-2 mb-6"
            style={{ background: cover, color: '#fff' }}
            data-testid="plan-continue-btn"
          >
            <Play className="h-4 w-4" />
            {t('generated.studyplandetailpage_continue_day_5d23')} {progress.current_day}
          </Button>
        ) : (
          <>
            <div
              className="w-full rounded-2xl py-4 text-base font-semibold gap-2 mb-3 flex items-center justify-center"
              style={{ background: `${cover}22`, color: cover, border: `1px solid ${cover}` }}
            >
              <CheckCircle2 className="h-5 w-5" />
              {t('generated.studyplandetailpage_plan_complete_ebd4')}
            </div>
            {plan.is_custom && completed.size >= plan.length_days && (
              <button
                type="button"
                onClick={() => navigate(`/study-plans/${planId}/recap`)}
                className="group w-full text-left rounded-2xl p-4 mb-6 flex items-center gap-3 transition-[transform,box-shadow] hover:-translate-y-[1px] hover:shadow-lg"
                style={{ background: 'var(--glass-bg)', border: `1px solid ${cover}55` }}
                data-testid="plan-recap-card"
              >
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${cover}22` }}>
                  <ScrollText className="h-5 w-5" style={{ color: cover }} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>Your plan recap</p>
                  <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>What you studied and your key takeaways</p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 opacity-60 transition-transform group-hover:translate-x-0.5" style={{ color: cover }} />
              </button>
            )}
          </>
        )}

        {/* Day list */}
        <h2 className="text-sm font-semibold mb-3 px-1" style={{ color: 'var(--theme-text)' }}>
          {t('generated.studyplandetailpage_days_189f')}
        </h2>
        <div className="space-y-2" data-testid="plan-detail-days">
          {plan.days.map((day) => {
            const isComplete = completed.has(day.day_number);
            const isCurrent = enrolled && progress.current_day === day.day_number && !isComplete;
            const locked = !enrolled;
            return (
              <button
                key={day.day_number}
                onClick={() => !locked && navigate(`/study-plans/${planId}/day/${day.day_number}`)}
                disabled={locked}
                className="w-full text-left rounded-xl p-3 flex items-center gap-3 transition-all active:scale-[0.99]"
                style={{
                  background: isCurrent ? `${cover}1a` : 'var(--glass-bg)',
                  border: `1px solid ${isCurrent ? cover : 'var(--glass-border)'}`,
                  opacity: locked ? 0.6 : 1,
                  WebkitTapHighlightColor: 'transparent',
                }}
                data-testid={`plan-day-${day.day_number}`}
              >
                {isComplete ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0" style={{ color: cover }} />
                ) : (
                  <Circle className="h-5 w-5 shrink-0" style={{ color: 'var(--theme-textSecondary)' }} />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-text)' }}>
                    {day.title}
                  </p>
                  {day.passages?.length > 0 && (
                    <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--theme-textSecondary)' }}>
                      {day.passages.map((p) => p.label).join(' · ')}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {/* Menu sheet */}
      {showMenu && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowMenu(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl p-2"
            style={{ background: 'var(--theme-background)', border: '1px solid var(--glass-border)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {enrolled && (
              <button
                onClick={leavePlan}
                className="w-full text-left text-sm font-medium px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10"
                data-testid="plan-leave-btn"
              >
                {t('generated.studyplandetailpage_leave_plan_a060')}
              </button>
            )}
            {plan.is_custom && (
              <button
                onClick={shareCustomPlan}
                className="w-full text-left text-sm font-medium px-4 py-3 rounded-xl flex items-center gap-2"
                style={{ color: 'var(--theme-text)' }}
                data-testid="plan-share-btn"
              >
                <Share2 className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
                Share this plan
              </button>
            )}
            {plan.is_custom && (
              <button
                onClick={deleteCustomPlan}
                className="w-full text-left text-sm font-medium px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                data-testid="plan-delete-btn"
              >
                <Trash2 className="h-4 w-4" />
                Delete custom plan
              </button>
            )}
            <button
              onClick={() => setShowMenu(false)}
              className="w-full text-left text-sm font-medium px-4 py-3 rounded-xl flex items-center gap-2"
              style={{ color: 'var(--theme-textSecondary)' }}
            >
              <X className="h-4 w-4" />
              {t('generated.studyplandetailpage_cancel_3beb')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
